import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { registry } from "@/api/client";
import { Field } from "@/components/Field";
import { PageHead } from "@/components/PageHead";
import { Select } from "@/components/inputs";
import { useMutationFeedback } from "@/lib/hooks/useMutationFeedback";
import { RQ } from "@/modules/registry/parts_common";
import { StockPicker, type StockPick } from "@/modules/registry/StockPicker";

import { ActionRow } from "@/components/ActionRow";
/** Ship & receive — CUSTODY ONLY. Nothing here changes who owns anything.
 *
 *  WHY IT IS A SEPARATE PAGE FROM TRANSFER. Ownership and custody are
 *  independent axes (REGISTRY.md §10) and D-82 exists to keep them apart:
 *  shipping is a PERIOD, so custody stays with the sender until the receiver
 *  accepts. Transfer is a SALE and may open a transit as a convenience; this
 *  page is for every custody move that is not a sale — sending a part to a
 *  repairer, moving stock between your own depots, sending goods you sold
 *  earlier without despatching, and accepting what someone sent you.
 *
 *  The gap it closes: before this page there was no way to ship a PART at all.
 *  /ship, /receive and /ship/cancel have always taken `part_ids`; nothing in
 *  the console called them. Assets could only be shipped one at a time, from
 *  inside the detail drawer, which is 50 drawer visits for a lot of 50.
 *
 *  SEARCH IS BY CUSTODIAN, not owner. You ship what you HOLD — a repairer
 *  sending a customer's vehicle back owns none of it.
 */
export function RegistryShipPage() {
  const qc = useQueryClient();
  const companies = useQuery({ queryKey: [...RQ, "companies"], queryFn: registry.companies });
  const opts = companies.data?.companies ?? [];
  const invalidate = () => qc.invalidateQueries({ queryKey: [...RQ] });

  // ---- send ---------------------------------------------------------------
  const [holder, setHolder] = useState("");
  const [to, setTo] = useState("");
  const [picked, setPicked] = useState<StockPick[]>([]);
  const sendFb = useMutationFeedback();
  const send = useMutation({
    mutationFn: () => registry.ship({
      // /ship takes ids by TYPE, and a part's id is its UUID rather than its
      // serial — StockPick carries both so this split is the only place that
      // has to know.
      asset_ids: picked.filter((p) => p.kind === "asset").map((p) => p.apiId),
      part_ids: picked.filter((p) => p.kind === "part").map((p) => p.apiId),
      to_company: to,
    }),
    onSuccess: () => { sendFb.ok(); setPicked([]); void invalidate(); },
    onError: sendFb.fail,
  });

  // ---- receive ------------------------------------------------------------
  const [arrivingAt, setArrivingAt] = useState("");
  const inFb = useMutationFeedback();
  // `incoming_to` is the D-82 inbox: custodian_pending = this company. Derived,
  // so it needs a server filter — pulling every row and filtering in the
  // browser would break the O(page) rule on the page that grows with the fleet.
  const inAssets = useQuery({
    queryKey: [...RQ, "incoming", "assets", arrivingAt],
    queryFn: () => registry.assets({ incoming_to: arrivingAt, limit: "200" }),
    enabled: !!arrivingAt,
  });
  const inParts = useQuery({
    queryKey: [...RQ, "incoming", "parts", arrivingAt],
    queryFn: () => registry.parts({ incoming_to: arrivingAt, limit: "200" }),
    enabled: !!arrivingAt,
  });

  const incoming = [
    ...(inAssets.data?.assets ?? []).map((a) => ({
      key: `a:${a.asset_id}`, kind: "asset" as const, apiId: a.asset_id,
      id: a.asset_id, from: a.custodian ?? a.custodian_id,
      what: [a.label, a.model].filter(Boolean).join(" · "), since: a.shipped_at,
    })),
    ...(inParts.data?.parts ?? []).map((p) => ({
      key: `p:${p.part_id}`, kind: "part" as const, apiId: p.part_id,
      id: p.serial_no, from: p.custodian ?? p.custodian_id,
      what: [p.kind_label, p.model].filter(Boolean).join(" · "), since: p.shipped_at,
    })),
  ];

  // ---- outgoing ----------------------------------------------------------
  // What this company still HOLDS and has addressed elsewhere. `custodian` +
  // `in_transit` together: custodian alone is everything settled on my shelf,
  // and incoming_to is the other direction.
  const [sentFrom, setSentFrom] = useState("");
  const outFb = useMutationFeedback();
  const outAssets = useQuery({
    queryKey: [...RQ, "outgoing", "assets", sentFrom],
    queryFn: () => registry.assets({ custodian: sentFrom, in_transit: "true", limit: "200" }),
    enabled: !!sentFrom,
  });
  const outParts = useQuery({
    queryKey: [...RQ, "outgoing", "parts", sentFrom],
    queryFn: () => registry.parts({ custodian: sentFrom, in_transit: "true", limit: "200" }),
    enabled: !!sentFrom,
  });
  const outgoing = [
    ...(outAssets.data?.assets ?? []).map((a) => ({
      key: `a:${a.asset_id}`, kind: "asset" as const, apiId: a.asset_id,
      id: a.asset_id, to: a.custodian_pending,
      what: [a.label, a.model].filter(Boolean).join(" · "), since: a.shipped_at,
    })),
    ...(outParts.data?.parts ?? []).map((p) => ({
      key: `p:${p.part_id}`, kind: "part" as const, apiId: p.part_id,
      id: p.serial_no, to: p.custodian_pending,
      what: [p.kind_label, p.model].filter(Boolean).join(" · "), since: p.shipped_at,
    })),
  ];
  const [cancelling, setCancelling] = useState<string[]>([]);
  const chosenOut = outgoing.filter((r) => cancelling.includes(r.key));
  const cancel = useMutation({
    mutationFn: () => registry.cancelShipment({
      asset_ids: chosenOut.filter((r) => r.kind === "asset").map((r) => r.apiId),
      part_ids: chosenOut.filter((r) => r.kind === "part").map((r) => r.apiId),
    }),
    onSuccess: () => { outFb.ok(); setCancelling([]); void invalidate(); },
    onError: outFb.fail,
  });

  const [accepting, setAccepting] = useState<string[]>([]);
  const toggle = (k: string) =>
    setAccepting((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  const chosen = incoming.filter((r) => accepting.includes(r.key));

  const accept = useMutation({
    mutationFn: () => registry.receive({
      asset_ids: chosen.filter((r) => r.kind === "asset").map((r) => r.apiId),
      part_ids: chosen.filter((r) => r.kind === "part").map((r) => r.apiId),
    }),
    onSuccess: () => { inFb.ok(); setAccepting([]); void invalidate(); },
    onError: inFb.fail,
  });

  return (
    <div className="mpage on">
      <PageHead eyebrow="Registry" title="Ship & receive"
        desc="Custody only — who is holding something, not who owns it. Shipping is a period: custody stays with the sender until the receiver accepts." />

      {/* ---------------------------------------------------------- send -- */}
      <div className="section-label">Ship</div>
      <div className="panel panel-pad">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 14 }}>
          <Field label="Holder — who has it now" req>
            <Select className="fill" value={holder}
                    onChange={(e) => { setHolder(e.target.value); setPicked([]); }}>
              <option value="">select…</option>
              {opts.map((c) => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
            </Select>
          </Field>
          <Field label="Ship to" req>
            <Select className="fill" value={to} onChange={(e) => setTo(e.target.value)}>
              <option value="">select…</option>
              {opts.map((c) => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
            </Select>
          </Field>
        </div>

        <div style={{ marginTop: 14 }}>
          <Field label="What is moving — search what the holder is holding" req>
            {/* custodian, NOT owner: you ship what you hold. */}
            <StockPicker company={holder} scopeBy="custodian" kinds={["asset", "part"]}
                         selected={picked} onChange={setPicked} />
          </Field>
        </div>

        <ActionRow>
          {sendFb.errorNode}
          <span className="cap">Custody moves when they accept, not now.</span>
          <button className="btn teal"
                  disabled={!picked.length || !holder || !to || holder === to || send.isPending}
                  onClick={() => send.mutate()}>
            {picked.length > 1 ? `Ship ${picked.length}` : "Ship"}
          </button>
        </ActionRow>
      </div>

      {/* ------------------------------------------------------- receive -- */}
      <div className="section-label">Receive — incoming</div>
      <div className="panel panel-pad">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 14 }}>
          <Field label="Arriving at" req>
            <Select className="fill" value={arrivingAt}
                    onChange={(e) => { setArrivingAt(e.target.value); setAccepting([]); }}>
              <option value="">select…</option>
              {opts.map((c) => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
            </Select>
          </Field>
        </div>

        {arrivingAt && (
          <div style={{ marginTop: 14 }}>
            {incoming.length === 0 ? (
              <div className="cap">Nothing is in transit to this company.</div>
            ) : (
              <>
                <div className="tblwrap">
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th style={{ width: 34 }}>
                          <input type="checkbox"
                                 checked={accepting.length === incoming.length}
                                 onChange={(e) => setAccepting(e.target.checked ? incoming.map((r) => r.key) : [])} />
                        </th>
                        <th>Identifier</th><th>What</th><th>From</th><th>Sent</th>
                      </tr>
                    </thead>
                    <tbody>
                      {incoming.map((r) => (
                        <tr key={r.key}>
                          <td><input type="checkbox" checked={accepting.includes(r.key)}
                                     onChange={() => toggle(r.key)} /></td>
                          <td className="mono">{r.id}</td>
                          <td>{r.what || "—"}</td>
                          <td>{r.from}</td>
                          <td className="cap">{r.since ? r.since.slice(0, 10) : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <ActionRow top={12}>
                  {inFb.errorNode}
                  <button className="btn teal" disabled={!chosen.length || accept.isPending}
                          onClick={() => accept.mutate()}>
                    Accept {chosen.length || ""}
                  </button>
                </ActionRow>
              </>
            )}
          </div>
        )}
      </div>

      {/* ------------------------------------------------------ outgoing -- */}
      <div className="section-label">Outgoing — awaiting acceptance</div>
      <div className="panel panel-pad">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 14 }}>
          <Field label="Sent by" req>
            <Select className="fill" value={sentFrom}
                    onChange={(e) => { setSentFrom(e.target.value); setCancelling([]); }}>
              <option value="">select…</option>
              {opts.map((c) => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
            </Select>
          </Field>
        </div>

        {sentFrom && (
          <div style={{ marginTop: 14 }}>
            {outgoing.length === 0 ? (
              <div className="cap">Nothing despatched and unaccepted.</div>
            ) : (
              <>
                <div className="tblwrap">
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th style={{ width: 34 }}>
                          <input type="checkbox" checked={cancelling.length === outgoing.length}
                                 onChange={(e) => setCancelling(e.target.checked ? outgoing.map((r) => r.key) : [])} />
                        </th>
                        <th>Identifier</th><th>What</th><th>Going to</th><th>Sent</th>
                      </tr>
                    </thead>
                    <tbody>
                      {outgoing.map((r) => (
                        <tr key={r.key}>
                          <td><input type="checkbox" checked={cancelling.includes(r.key)}
                                     onChange={() => setCancelling((s) => s.includes(r.key)
                                       ? s.filter((x) => x !== r.key) : [...s, r.key])} /></td>
                          <td className="mono">{r.id}</td>
                          <td>{r.what || "—"}</td>
                          <td>{r.to}</td>
                          <td className="cap">{r.since ? r.since.slice(0, 10) : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center", justifyContent: "flex-end" }}>
                  {outFb.errorNode}
                  {/* Custody never moved, so calling it off restores nothing —
                      it only clears the destination. Said plainly because
                      "cancel" on a shipment sounds like it should claw
                      something back. */}
                  <span className="cap">Custody never moved — this only clears the destination.</span>
                  <button className="btn red"
                          disabled={!chosenOut.length || cancel.isPending}
                          onClick={() => cancel.mutate()}>
                    Cancel {chosenOut.length || ""}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
